import { Download, ExternalLink, Eye, Play } from "lucide-react";
import { Button } from "@shared/components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@shared/components/ui/tooltip";
import type { MarketingFileView } from "@shared/lib/marketing-shared";

const actionClass = "text-muted-foreground max-sm:size-11";

/** Bouton à icône seule : `aria-label` et infobulle portent le même verbe. */
function IconAction({
  label,
  children,
  onClick,
  href,
  download = false,
  external = false,
  disabled = false,
}: {
  label: string;
  children: React.ReactNode;
  onClick?: () => void;
  href?: string;
  download?: boolean;
  external?: boolean;
  disabled?: boolean;
}) {
  if (disabled) {
    return (
      <Button aria-label={label} className={actionClass} disabled size="icon-sm" type="button" variant="ghost">
        {children}
      </Button>
    );
  }
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        {href ? (
          <Button asChild className={actionClass} size="icon-sm" variant="ghost">
            <a
              aria-label={label}
              download={download ? "" : undefined}
              href={href}
              rel={external ? "noopener noreferrer" : undefined}
              target={external ? "_blank" : undefined}
            >
              {children}
            </a>
          </Button>
        ) : (
          <Button aria-label={label} className={actionClass} onClick={onClick} size="icon-sm" type="button" variant="ghost">
            {children}
          </Button>
        )}
      </TooltipTrigger>
      <TooltipContent>{label}</TooltipContent>
    </Tooltip>
  );
}

const withSize = (file: MarketingFileView) => (file.size ? `${file.label} (${file.size})` : file.label);

export function DownloadAction({ file }: { file: MarketingFileView }) {
  return (
    <IconAction disabled={file.missing} download href={file.downloadUrl} label={`Télécharger ${withSize(file)}`}>
      <Download />
    </IconAction>
  );
}

/** Lire (vidéo), Aperçu (image) ou Ouvrir (PDF, nouvel onglet) ; rien pour un texte ou une archive. */
export function ViewAction({ file, onPreview }: { file: MarketingFileView; onPreview: (key: string) => void }) {
  if (file.kind === "video") {
    return (
      <IconAction disabled={file.missing} label={`Lire ${file.label}`} onClick={() => onPreview(file.key)}>
        <Play />
      </IconAction>
    );
  }
  if (file.kind === "image") {
    return (
      <IconAction disabled={file.missing} label={`Aperçu ${file.label}`} onClick={() => onPreview(file.key)}>
        <Eye />
      </IconAction>
    );
  }
  if (file.kind === "pdf") {
    return (
      <IconAction disabled={file.missing} external href={file.url} label={`Ouvrir ${file.label}`}>
        <ExternalLink />
      </IconAction>
    );
  }
  return null;
}
